import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchAbsenceTypes } from "../../../api/absence/type";
import { fetchUserAbsenceStats } from "../../../api/user";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

/**
 * Modern Absence Type Selector Component
 * 
 * Features:
 * - Fast absence type fetching with debounced search
 * - Single selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Object} props.selectedAbsenceType - Selected absence type object
 * @param {Function} props.onChange - Callback when selection changes (receives absence type object)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {Array} props.filters - Additional filters to apply
 * @param {number} props.userId - User ID to fetch absence stats for (optional, defaults to current user)
 */
export default function ModernAbsenceTypeSelector({
  open = false,
  selectedAbsenceType = null,
  onChange,
  onClose,
  filters = [],
  showHeader = true,
  userId = null,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedId, setSelectedId] = useState(selectedAbsenceType?.id || null);
  const [absenceStats, setAbsenceStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const currentUser = useSelector((state) => state.session.currentUser);
  const targetUserId = userId || currentUser?.id;

  const perPage = 20;

  // Update selectedId when selectedAbsenceType prop changes
  useEffect(() => {
    if (open) {
      setSelectedId(selectedAbsenceType?.id || null);
    }
  }, [selectedAbsenceType, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setAbsenceTypes([]);
    } else {
      setAbsenceTypes([]);
    }
  }, [open]);

  // Fetch absence stats when dialog opens
  useEffect(() => {
    if (open && targetUserId) {
      setIsLoadingStats(true);
      const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];
      const yearEnd = new Date().toISOString().split('T')[0];
      
      fetchUserAbsenceStats(targetUserId, { from: yearStart, to: yearEnd })
        .then((response) => {
          if (response.status === 200) {
            setAbsenceStats(response.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching absence stats:", err);
        })
        .finally(() => {
          setIsLoadingStats(false);
        });
    } else {
      setAbsenceStats(null);
    }
  }, [open, targetUserId]);

  // Create search function that will be debounced
  const searchFunction = useCallback(async (query, page, currentFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        per_page: perPage,
        sortBy: "title",
        sort: "asc",
        fields: "id,title,displayTitle,code,max_duration_days,max_occurrences_per_year,description",
        filters: [...currentFilters],
      };

      // Add query if provided
      if (query && query.trim()) {
        params.query = query.trim();
      }

      const response = await fetchAbsenceTypes(params);

      if (response && response.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        
        if (page === 1) {
          setAbsenceTypes(data);
        } else {
          setAbsenceTypes((prev) => [...prev, ...data]);
        }

        setTotalCount(response.total_count || response.data.length || data.length);
        setHasMore(
          data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setAbsenceTypes([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching absence types:", err);
      setError("Kunne ikke laste fraværstyper");
      setAbsenceTypes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction changes
  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(searchFunction, 300);
    
    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchFunction]);

  // Memoize filters to prevent infinite loops
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch absence types when search query changes
  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    // Always fetch when dialog opens (even without query)
    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery, 1, filtersRef.current);

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open]);

  // Load more absence types
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery, nextPage, filtersRef.current);
    }
  }, [isLoading, hasMore, currentPage, searchQuery]);

  // Select absence type
  const selectAbsenceType = useCallback(
    (absenceType) => {
      console.log("🔵 ModernAbsenceTypeSelector: selectAbsenceType called", absenceType);
      setSelectedId(absenceType.id);

      if (onChange) {
        console.log("🔵 ModernAbsenceTypeSelector: Calling onChange with", absenceType);
        onChange(absenceType);
      } else {
        console.warn("⚠️ ModernAbsenceTypeSelector: onChange is not defined");
      }

      // Auto-close after selection
      if (onClose) {
        setTimeout(() => {
          console.log("🔵 ModernAbsenceTypeSelector: Closing dialog");
          onClose();
        }, 300);
      }
    },
    [onChange, onClose]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedId(null);
    if (onChange) {
      onChange(null);
    }
  }, [onChange]);

  // Check if absence type is selected
  const isSelected = useCallback(
    (absenceTypeId) => {
      return selectedId === absenceTypeId;
    },
    [selectedId]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Velg fraværstype
            </h3>
            <div className="flex items-center space-x-2">
              {selectedId && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter fraværstype..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter fraværstype..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Absence Type List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && absenceTypes.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster fraværstyper...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : absenceTypes.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen fraværstyper funnet"
                  : "Søk etter fraværstyper for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {absenceTypes.slice(0, 20).map((absenceType) => {
              const selected = isSelected(absenceType.id);
              
              return (
                <div
                  key={absenceType.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("🔵 ModernAbsenceTypeSelector: Clicked on absence type", absenceType);
                    selectAbsenceType(absenceType);
                  }}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300 hover:border-blue-400"
                        }`}
                      >
                        {selected && (
                          <CheckIcon className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>

                    {/* Absence Type Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {absenceType.displayTitle || absenceType.title}
                      </p>
                      {absenceType.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {absenceType.description}
                        </p>
                      )}
                      {/* Show stats for each absence type */}
                      {!isLoadingStats && absenceStats && (() => {
                        const code = absenceType.code;
                        const stats = absenceStats[code];
                        
                        if (!stats || stats.error) {
                          // Fallback: show max days/occurrences only for self_certified
                          if (code === "self_certified" && (absenceType.max_duration_days || absenceType.max_occurrences_per_year)) {
                            return (
                              <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                                {absenceType.max_duration_days && (
                                  <span className="flex items-center">
                                    <CalendarIcon className="h-3 w-3 mr-1" />
                                    Maks {absenceType.max_duration_days} {absenceType.max_duration_days === 1 ? "dag" : "dager"}
                                  </span>
                                )}
                                {absenceType.max_occurrences_per_year && (
                                  <span>
                                    Maks {absenceType.max_occurrences_per_year} {absenceType.max_occurrences_per_year === 1 ? "gang" : "ganger"} per år
                                  </span>
                                )}
                              </div>
                            );
                          }
                          if (code === "compensatory_time_off") {
                            return (
                              <div className="mt-1 text-xs text-gray-500 italic">
                                Basert på faktiske innstående timer
                              </div>
                            );
                          }
                          return null;
                        }
                        
                        // Self-certified: show max days/occurrences and remaining
                        if (code === "self_certified") {
                          return (
                            <div className="mt-2 space-y-1">
                              {(absenceType.max_duration_days || absenceType.max_occurrences_per_year) && (
                                <div className="flex items-center space-x-3 text-xs text-gray-400">
                                  {absenceType.max_duration_days && (
                                    <span className="flex items-center">
                                      <CalendarIcon className="h-3 w-3 mr-1" />
                                      Maks {absenceType.max_duration_days} {absenceType.max_duration_days === 1 ? "dag" : "dager"}
                                    </span>
                                  )}
                                  {absenceType.max_occurrences_per_year && (
                                    <span>
                                      Maks {absenceType.max_occurrences_per_year} {absenceType.max_occurrences_per_year === 1 ? "gang" : "ganger"} per år
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="text-xs text-gray-600">
                                Brukt: {stats.used_days || 0} dager · Gjenstående: {stats.remaining_days || 0} dager
                              </div>
                              {stats.max_occurrences && (
                                <div className="text-xs text-gray-600">
                                  Forekomster: {stats.used_occurrences || 0} / {stats.max_occurrences}
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // Vacation: show used and remaining days
                        if (code === "vacation") {
                          return (
                            <div className="mt-2 text-xs text-gray-600">
                              Brukt: {stats.used_days || 0} dager · Gjenstående: {stats.remaining_days || 0} dager
                            </div>
                          );
                        }
                        
                        // Child sick leave: show used and remaining days
                        if (code === "child_sick_leave") {
                          return (
                            <div className="mt-2 text-xs text-gray-600">
                              Brukt: {stats.used_days || 0} dager · Gjenstående: {stats.remaining_days || 0} dager
                            </div>
                          );
                        }
                        
                        // Compensatory time off: show hours
                        if (code === "compensatory_time_off") {
                          return (
                            <div className="mt-2 space-y-1">
                              <div className="text-xs text-gray-500 italic">
                                Basert på faktiske innstående timer
                              </div>
                              <div className="text-xs text-gray-600">
                                Gjenstående: {stats.earned_hours_left || 0} timer
                              </div>
                            </div>
                          );
                        }
                        
                        // Sick leave: show info message
                        if (code === "sick_leave") {
                          return (
                            <div className="mt-2 space-y-1">
                              <div className="text-xs text-gray-500 italic">
                                Basert på legeerklæring
                              </div>
                              {stats && (
                                <div className="text-xs text-gray-600">
                                  Brukt: {stats.used_days || 0} dager
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        return null;
                      })()}
                      
                      {/* Show loading state while fetching stats */}
                      {isLoadingStats && (
                        <div className="mt-1 text-xs text-gray-400">
                          Laster statistikk...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && absenceTypes.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere fraværstyper
            </button>
          </div>
        )}

        {isLoading && absenceTypes.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
}

